import { Injectable } from '@angular/core';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import {
  Firestore,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  getDocs,
  addDoc,
  serverTimestamp,
  writeBatch,
} from '@angular/fire/firestore';
import { BehaviorSubject, Observable } from 'rxjs';

export interface AppNotification {
  id: string; // Firestore doc ID
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  isRead: boolean;
  createdAt: any; // Timestamp
  link?: string; // Optional link to navigate to
  bookingId?: string; // Optional booking ID
}

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private notificationsSubject = new BehaviorSubject<AppNotification[]>([]);
  public notifications$ = this.notificationsSubject.asObservable();

  private unreadCountSubject = new BehaviorSubject<number>(0);
  public unreadCount$ = this.unreadCountSubject.asObservable();

  private unsubscribe: (() => void) | null = null;

  constructor(
    private auth: Auth,
    private firestore: Firestore,
  ) {
    this.init();
  }

  private init() {
    onAuthStateChanged(this.auth, async (user) => {
      if (user) {
        // ── WORKER SUPPORT: subscribe to the parent vendor's notifications ──
        let effectiveUid = user.uid;
        try {
          const snap = await getDoc(doc(this.firestore, 'users-list', user.uid));
          if (snap.exists()) {
            const data = snap.data() as any;
            if (data.role === 'worker' && data.parentUid) {
              effectiveUid = data.parentUid;
            }
          }
        } catch (_) { /* fall back to own uid */ }
        // ── END WORKER SUPPORT ───────────────────────────────────────────────
        this.subscribeToNotifications(effectiveUid);
      } else {
        this.cleanup();
      }
    });
  }

  private subscribeToNotifications(userId: string) {
    if (this.unsubscribe) {
      this.unsubscribe();
    }

    const notificationsRef = collection(
      this.firestore,
      `users/${userId}/notifications`,
    );
    const q = query(notificationsRef, orderBy('createdAt', 'desc'));

    this.unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const notifications: AppNotification[] = [];
        let unread = 0;

        snapshot.forEach((doc) => {
          const data = doc.data() as any;
          const notification: AppNotification = {
            id: doc.id,
            title: data.title,
            message: data.message,
            type: data.type || 'info', // Default to info
            isRead: data.isRead || false,
            createdAt: data.createdAt,
            link: data.link,
            bookingId: data.bookingId,
          };
          notifications.push(notification);

          if (!notification.isRead) {
            unread++;
          }
        });

        this.notificationsSubject.next(notifications);
        this.unreadCountSubject.next(unread);
      },
      (error) => {
        console.error('Error fetching notifications: ', error);
      },
    );
  }

  markAsRead(userId: string, notificationId: string) {
    const docRef = doc(
      this.firestore,
      `users/${userId}/notifications/${notificationId}`,
    );
    updateDoc(docRef, { isRead: true }).catch((err) =>
      console.error('Error marking read', err),
    );
  }

  async markAllAsRead(userId: string) {
    const currentUnread = this.notificationsSubject.value.filter(
      (n) => !n.isRead,
    );
    currentUnread.forEach((n) => {
      this.markAsRead(userId, n.id);
    });
  }

  async markBookingNotificationsAsRead(bookingId: string, userId?: string) {
    try {
      const uid = userId || this.auth.currentUser?.uid;
      if (!uid) return;

      const notificationsRef = collection(
        this.firestore,
        `users/${uid}/notifications`,
      );
      const q = query(
        notificationsRef,
        where('isRead', '==', false),
      );
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const batch = writeBatch(this.firestore);
        let count = 0;
        snapshot.forEach((docSnap) => {
          const data = docSnap.data() as any;
          const matchesId = data.bookingId === bookingId;
          const matchesLink = data.link && data.link.includes(bookingId);
          
          if (matchesId || matchesLink) {
            batch.update(docSnap.ref, { isRead: true });
            count++;
          }
        });
        if (count > 0) {
          await batch.commit();
        }
      }
    } catch (error) {
      console.error('Error marking booking notifications as read:', error);
    }
  }

  async sendNotification(
    userId: string,
    notification: Partial<AppNotification>,
  ) {
    try {
      const notificationsRef = collection(
        this.firestore,
        `users/${userId}/notifications`,
      );
      await addDoc(notificationsRef, {
        title: notification.title,
        message: notification.message,
        type: notification.type || 'info',
        isRead: false,
        createdAt: serverTimestamp(),
        link: notification.link || '',
        bookingId: notification.bookingId || null,
      });
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  }

  async clearAllNotifications(userId: string) {
    try {
      const notificationsRef = collection(
        this.firestore,
        `users/${userId}/notifications`,
      );
      const notifications = this.notificationsSubject.value;

      const batch = writeBatch(this.firestore);

      notifications.forEach((n) => {
        const docRef = doc(
          this.firestore,
          `users/${userId}/notifications/${n.id}`,
        );
        batch.delete(docRef);
      });

      await batch.commit();
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  }

  private cleanup() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
    this.notificationsSubject.next([]);
    this.unreadCountSubject.next(0);
  }
}
